<template>
  <div style="height: 100%">
    <v-layout
      justify-center
      @mouseleave="closeHoverAndMenu"
      ref="networkDiagramCable"
      style="position: relative"
    >
      <div @mouseenter="hoverActive = true" style="width: fit-content">
        <v-layout
          row
          align-end
          justify-center
          wrap
          class="network-diagram-cable cable-text"
          style="height: 30px"
          :class="{
            sibling: hasPreviousSibling,
            'rounded-cable': hasPreviousSibling
          }"
          :data-object-uuid="cableData.cb_uuid"
        >
          <div
            v-for="(item, index) in structureTop"
            :key="item.varName"
            v-if="cableData[item.varName] !== null"
          >
            <label v-if="item.label">{{ item.label }}: </label>
            <span style="color: black; font-weight: bold">
              {{ cableData[item.varName] }}
            </span>
            <span v-if="!isLastItem(index, nrFieldsTop)" style="margin: 0 3px"
              >|</span
            >
          </div>
        </v-layout>

        <v-layout
          row
          justify-space-around
          class="cable-text"
          :class="{ sibling: hasNextSibling }"
        >
          <v-flex shrink>
            <v-layout column align-start>
              <div
                v-for="item in structureBottom"
                :key="item.varName"
                style="width: fit-content; max-width: 125px; text-align: left"
                v-if="cableData[item.varName] !== null"
              >
                <label v-if="item.label">{{ item.label }}: </label>
                <span style="color: black; font-weight: bold">
                  {{ cableData[item.varName] }}
                </span>
              </div>

              <div style="width: fit-content">
                <label>{{ $t("Length") }}: </label>
                <span style="color: black; font-weight: bold">
                  {{ length }}m
                </span>
              </div>
              <div style="width: fit-content">
                <label>{{ $t("TotalLength") }}: </label>
                <span style="color: black; font-weight: bold">
                  {{ totalLength }}m
                </span>
              </div>
            </v-layout>
          </v-flex>
        </v-layout>
      </div>

      <div
        v-if="hoverActive && !zoomIn"
        class="network-diagram-hover-container"
        @contextmenu.prevent="openObjectMenu"
      >
        <MoreSVG class="network-diagram-hover-button" />
      </div>
    </v-layout>

    <div
      :class="{ sibling: hasNextSibling }"
      :style="{ height: nextSiblingBorderHeight }"
    />
  </div>
</template>

<script>
import MoreSVG from "@/assets/home/tabs/network/more.svg";

export default {
  name: "NetworkDiagramCable",

  components: { MoreSVG },

  props: {
    /**
     * Data of the node being showed.
     * */
    cableData: {
      type: Object,
      required: true
    },

    /**
     * If the current node has siblings being represented before/above.
     */
    hasPreviousSibling: {
      type: Boolean
    },

    /**
     * If the current node has siblings being represented after/below.
     */
    hasNextSibling: {
      type: Boolean
    },

    /**
     * If the object is being showed in the zoom in window.
     */
    zoomIn: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      /** Fields of the cable objects to be showed.  */
      structureTop: [
        { label: this.$i18n.t("ID"), varName: "cb_id" },
        { label: this.$i18n.t("ParentID"), varName: "cb_parent_id" }
      ],

      structureBottom: [
        { label: this.$i18n.tc("Label", 1), varName: "cb_label" },
        { label: this.$i18n.tc("Reference", 1), varName: "cb_material_id" },
        { label: this.$i18n.t("Length"), varName: "cb_total_length" },
        { label: this.$i18n.t("SlackCount"), varName: "cb_slack_count" },
        { label: this.$i18n.t("Capacity"), varName: "cb_capacity" },
        { label: this.$i18n.t("FibersInUse"), varName: "cb_fibers_in_use" },
        { varName: "pw_instalation_type" },
        { varName: "cb_comment" }
      ],

      /**
       * If the mouse is hovering the cable or not
       */
      hoverActive: false,

      /**
       * If the cable object menu is open or not.
       */
      menuOpen: false,

      nextSiblingBorderHeight: "calc(100 % - 70px)"
    };
  },

  computed: {
    /**
     * Calculates the number of fields that should appear for each cable.
     * returns {number}
     */
    nrFieldsTop() {
      return this.structureTop.length;
    },

    /**
     * Calculates the number of fields that should appear for each cable.
     * returns {number}
     */
    nrFieldsBottom() {
      return this.structureBottom.length;
    },

    length() {
      if (this.cableData.cb_measured_length) {
        return this.cableData.cb_measured_length;
      } else {
        return "\u2014";
      }
    },

    totalLength() {
      if (this.cableData.cb_total_length) {
        return this.cableData.cb_total_length;
      } else {
        return "\u2014";
      }
    }
  },

  mounted() {
    this.$nextTick(() => {
      let diagramCableHeight = this.$refs.networkDiagramCable.offsetHeight - 20;
      this.nextSiblingBorderHeight =
        "calc(100% - " + diagramCableHeight + "px)";
    });
  },

  methods: {
    /**
     * Checks if the given field is the last one of the cable labeling.
     * param {number} index - index of the field in the cable labeling structure.
     * returns {boolean}
     */
    isLastItem(index, nrFields) {
      return index === nrFields - 1;
    },

    /**
     * Closes the menu and makes the hover style disappear.
     */
    closeHoverAndMenu() {
      this.menuOpen = false;
      this.hoverActive = false;
    },

    /**
     * Opens the object menu on top of the selected object.
     */
    openObjectMenu(e) {
      let data = {
        positioningInfo: {
          x: e.x,
          y: e.y
        },
        selectedObject: {
          type: "cable",
          nodeType: undefined,
          objectData: this.cableData
        }
      };
      this.$store.dispatch("OPEN_NETWORK_DIAGRAM_OBJECT_MENU", data);
    }
  }
};
</script>

<style scoped>
.network-diagram-cable {
  width: 200px;
  border-bottom: 1px solid #b9b9b9;
  margin-top: 5px;
}

.rounded-cable {
  border-bottom-left-radius: 10px;
}

.sibling {
  border-left: 1px solid #b9b9b9;
}

.cable-text {
  margin: 0;
  font-size: 9px;
  padding: 2px;
  opacity: 0.83;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Content/Tabs/Network/NetworkDiagram/NetworkDiagramCable.vue